<template>
  <div class="chat-sidebar">
    <div class="chat-sidebar-header">
      <h6 class="chat-sidebar-title">Chat</h6>
    </div>
    <div class="chat-messages" ref="chatMessages">
      <div v-for="(message, index) in messages" :key="index" class="chat-message-wrapper" :class="{ 'user-message-wrapper': message.role === 'user', 'ai-message-wrapper': message.role === 'ai' }">
        <!-- AI Message - Left Aligned -->
        <div v-if="message.role === 'ai'" class="chat-message ai-message">
          <div class="chat-message-avatar ai-avatar">
            <img :src="aiLogo" alt="AI" />
          </div>
          <div class="chat-message-content">
            <div class="chat-message-text">{{ message.text }}</div>
            <div class="chat-message-actions">
              <button class="chat-action-icon" @click="handleLike(index)">
                <img :src="likeIcon" alt="Like" />
              </button>
              <button class="chat-action-icon" @click="handleDislike(index)">
                <img :src="dislikeIcon" alt="Dislike" />
              </button>
            </div>
          </div>
        </div>
        <!-- User Message - Right Aligned -->
        <div v-if="message.role === 'user'" class="chat-message user-message">
          <div class="chat-message-content">
            <div class="chat-message-text">{{ message.text }}</div>
          </div>
          <div class="chat-message-avatar user-avatar">
            <span>{{ userInitials }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="chat-input-section">
      <div class="chat-input-box chat-sidebar-input-box">
        <div class="chat-input-content">
          <textarea
            v-model="inputMessage"
            class="chat-input-field chat-sidebar-input-field chat-sidebar-textarea"
            placeholder="Let's create a dashboard....."
            @keydown="handleKeyPress"
            @input="handleInput"
            @click="handleInputClick"
            @focus="handleInputFocus"
            ref="chatInput"
            rows="3"
            tabindex="0"
            style="color: rgba(0, 0, 0, 1) !important; caret-color: rgba(0, 0, 0, 1) !important;"
          ></textarea>
        </div>
        <div class="chat-input-actions">
          <button class="chat-action-btn visual-edit-btn" @click="handleVisualEdit" type="button">
            <img :src="visualEditIcon" alt="Visual Edit" class="visual-edit-icon" />
            <span class="visual-edit-text">Visual edit</span>
          </button>
          <button class="chat-action-btn send-btn" @click.stop="sendMessage" type="button" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate color="primary" size="20" width="2"></v-progress-circular>
            <img v-else :src="sendIcon" alt="Send" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ChatSidebar",
  props: {
    initialMessage: {
      type: String,
      default: null,
    },
    aiResponse: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      inputMessage: "",
      messages: [],
      aiLogo: require("@/assets/Images/FDPal/aiLogo.png"),
      likeIcon: require("@/assets/Images/FDPal/like.svg"),
      dislikeIcon: require("@/assets/Images/FDPal/dislike.svg"),
      sendIcon: require("@/assets/Images/FDPal/send-2.svg"),
      visualEditIcon: require("@/assets/Images/Dashboard/visual-edit-icon.svg"), // Placeholder - user will provide icon
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser"]),
    userInitials() {
      const user = this.getUser;
      if (user && user.firstName && user.lastName) {
        return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
      } else if (user && user.username) {
        return user.username.charAt(0).toUpperCase();
      }
      return "U";
    },
  },
  mounted() {
    if (this.initialMessage) {
      // Add initial message as user message
      this.messages.push({
        text: this.initialMessage,
        role: "user",
      });
    }
    
    // Add AI response if provided
    if (this.aiResponse) {
      this.$nextTick(() => {
        this.messages.push({
          text: this.aiResponse,
          role: "ai",
        });
        this.scrollToBottom();
      });
    }
    
    // Focus input after mount
    this.focusInput();
  },
  watch: {
    initialMessage(newVal) {
      if (newVal && this.messages.length === 0) {
        this.messages.push({
          text: newVal,
          role: "user",
        });
        this.$nextTick(() => {
          if (this.aiResponse) {
            this.messages.push({
              text: this.aiResponse,
              role: "ai",
            });
          }
          this.scrollToBottom();
        });
      }
    },
    aiResponse(newVal) {
      if (newVal && this.messages.length > 0) {
        // Check if AI response already exists
        const hasAIResponse = this.messages.some(msg => msg.role === "ai");
        if (!hasAIResponse) {
          this.messages.push({
            text: newVal,
            role: "ai",
          });
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      }
    },
  },
  methods: {
    sendMessage() {
      console.log("sendMessage called, inputMessage:", this.inputMessage);
      const message = this.inputMessage.trim();
      if (message) {
        this.messages.push({
          text: message,
          role: "user",
        });
        const userMessage = message;
        this.inputMessage = "";
        this.$nextTick(() => {
          this.scrollToBottom();
          // Emit message to parent - parent will handle API call and update aiResponse
          this.$emit("send-message", userMessage);
        });
      }
    },
    handleKeyPress(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    handleVisualEdit() {
      // Handle visual edit action
      this.$emit("visual-edit");
    },
    handleLike(index) {
      this.$emit("message-like", index);
    },
    handleDislike(index) {
      this.$emit("message-dislike", index);
    },
    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages;
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    },
    handleInput(event) {
      // Ensure input is working - v-model should handle this, but this ensures it works
      console.log("Input changed:", event.target.value);
      this.inputMessage = event.target.value;
    },
    handleInputClick(event) {
      console.log("Input clicked");
      event.stopPropagation();
      if (this.$refs.chatInput) {
        this.$refs.chatInput.focus();
      }
    },
    handleInputFocus(event) {
      console.log("Input focused");
      event.stopPropagation();
    },
    focusInput() {
      // Focus the input when component is mounted
      this.$nextTick(() => {
        if (this.$refs.chatInput) {
          this.$refs.chatInput.focus();
          console.log("Input focused programmatically");
        } else {
          console.log("Input ref not found");
        }
      });
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/chatSidebar.css';
</style>

<style>
/* Force black placeholder for ChatSidebar - non-scoped to override global styles */
/* Use unique class to override all other rules */
.chat-sidebar-textarea::placeholder,
.chat-sidebar-textarea:not(:focus)::placeholder,
.chat-sidebar-textarea:focus::placeholder,
.chat-sidebar-textarea::-webkit-input-placeholder,
.chat-sidebar-textarea:not(:focus)::-webkit-input-placeholder,
.chat-sidebar-textarea:focus::-webkit-input-placeholder,
.chat-sidebar-textarea::-moz-placeholder,
.chat-sidebar-textarea:not(:focus)::-moz-placeholder,
.chat-sidebar-textarea:focus::-moz-placeholder,
.chat-sidebar-textarea:-ms-input-placeholder,
.chat-sidebar-textarea:not(:focus):-ms-input-placeholder,
.chat-sidebar-textarea:focus:-ms-input-placeholder,
.chat-sidebar-textarea:-moz-placeholder,
.chat-sidebar-textarea:not(:focus):-moz-placeholder,
.chat-sidebar-textarea:focus:-moz-placeholder {
  color: rgba(0, 0, 0, 1) !important;
  opacity: 1 !important;
}

/* Also target with parent class for extra specificity */
.chat-sidebar .chat-sidebar-textarea::placeholder,
.chat-sidebar .chat-sidebar-textarea:not(:focus)::placeholder,
.chat-sidebar .chat-sidebar-textarea:focus::placeholder,
.chat-sidebar .chat-sidebar-textarea::-webkit-input-placeholder,
.chat-sidebar .chat-sidebar-textarea:not(:focus)::-webkit-input-placeholder,
.chat-sidebar .chat-sidebar-textarea:focus::-webkit-input-placeholder,
.chat-sidebar .chat-sidebar-textarea::-moz-placeholder,
.chat-sidebar .chat-sidebar-textarea:not(:focus)::-moz-placeholder,
.chat-sidebar .chat-sidebar-textarea:focus::-moz-placeholder,
.chat-sidebar .chat-sidebar-textarea:-ms-input-placeholder,
.chat-sidebar .chat-sidebar-textarea:not(:focus):-ms-input-placeholder,
.chat-sidebar .chat-sidebar-textarea:focus:-ms-input-placeholder,
.chat-sidebar .chat-sidebar-textarea:-moz-placeholder,
.chat-sidebar .chat-sidebar-textarea:not(:focus):-moz-placeholder,
.chat-sidebar .chat-sidebar-textarea:focus:-moz-placeholder {
  color: rgba(0, 0, 0, 1) !important;
  opacity: 1 !important;
}
</style>

